import React, { createContext, useContext, useState } from "react";
import { Invitation } from "../lib/supabase";

interface InvitationContextType {
  invitation: Invitation | null;
  loading: boolean;
  error: string;
  validateCode: (code: string) => Promise<boolean>;
  clearInvitation: () => void;
  refetchInvitation: () => Promise<void>;
}

const InvitationContext = createContext<InvitationContextType | undefined>(
  undefined
);

export const useInvitation = () => {
  const context = useContext(InvitationContext);
  if (context === undefined) {
    throw new Error("useInvitation must be used within an InvitationProvider");
  }
  return context;
};

export const InvitationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validateCode = async (code: string): Promise<boolean> => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/invitations/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: code.toUpperCase() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Invalid invitation code");
        return false;
      }

      const data = await response.json();
      setInvitation(data);
      return true;
    } catch (err: any) {
      setError("Failed to validate invitation code");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refetchInvitation = async (): Promise<void> => {
    if (!invitation?.code) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`/api/invitations/validate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: invitation.code.toUpperCase() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to refetch invitation");
        return;
      }

      const data = await response.json();
      setInvitation(data);
    } catch (err: any) {
      setError("Failed to refetch invitation");
    } finally {
      setLoading(false);
    }
  };

  const clearInvitation = () => {
    setInvitation(null);
    setError("");
  };

  const value = {
    invitation,
    loading,
    error,
    validateCode,
    clearInvitation,
    refetchInvitation,
  };

  return (
    <InvitationContext.Provider value={value}>
      {children}
    </InvitationContext.Provider>
  );
};
